import { HelpAnchor, findHelpAnchor } from "./anchors";
import { announcement } from "./registry";

/**
 * Below the desktop width the sidebar is a drawer that starts closed, so a
 * tour step pointing at a nav link opens it first, through the navbar's
 * toggle, and waits for the drawer to finish sliding in: the callout is
 * placed from the link's box, and a box taken mid-slide puts it on the
 * wrong side. On a desktop the link is already on screen and nothing happens.
 */
export const revealNavLink = (anchor: HelpAnchor) => async () => {
  const link = findHelpAnchor(anchor);
  if (!link || link.getBoundingClientRect().right > 0) {
    return;
  }
  document.querySelector<HTMLElement>('[aria-label="Open drawer"]')?.click();
  await settled(link);
};

/** Resolves once the element's box has stopped moving (or after a second). */
const settled = (element: HTMLElement) =>
  new Promise<void>((resolve) => {
    const started = Date.now();
    let last = element.getBoundingClientRect().right;
    const poll = () => {
      const { right } = element.getBoundingClientRect();
      if ((right === last && right > 0) || Date.now() - started > 1000) {
        resolve();
        return;
      }
      last = right;
      window.setTimeout(poll, 50);
    };
    window.setTimeout(poll, 50);
  });

/**
 * What's new, oldest first. Adding an announcement is a web-only change:
 * the API stores only what each user has seen, by the keys in `helpKeys`.
 * Media for an announcement with `video: true` is uploaded with
 * `make help-media ID=<id>`.
 */
export const ANNOUNCEMENTS = [
  announcement("pause-live", {
    title: "New: pause live data",
    body: "Press the LIVE badge to hold the dashboard still while the session keeps streaming, compare laps, then press it again to catch up.",
    released: "2026-10-10",
    // Pausing is about the session dashboard: introduce it there.
    route: "/eventdevice/:id",
    video: true,
    hints: [
      {
        route: "/eventdevice/:id",
        anchor: "eventdevice.live-pause",
        title: "Pause live data",
        body: "Press LIVE to pause the dashboard and compare laps. Press it again to resume.",
        placement: "bottom",
      },
    ],
  }),
  announcement("analysis-workspaces", {
    title: "New: Analysis workspaces",
    body: "Compare laps from any sessions at the same track, across events, and follow a car live. Press Analyze on any session to start.",
    // Launch night, 21 Oct (Pacific). Compared at 00:00 UTC, so the 22nd:
    // accounts created before 5 pm Pacific on the 21st get the dialog.
    released: "2026-10-22",
    // Introduced on the home page, where everyone lands.
    route: "/",
    video: true,
  }),
  announcement("teams", {
    title: "New: Teams",
    body: "Share a session or a whole car with your crew chief, coach or co-driver. They follow it live or go through it later, read only, on any account. Teams is part of Podium Pro.",
    released: "2026-10-27",
    // Introduced on the home page, where everyone lands.
    route: "/",
    video: true,
    tour: [
      {
        anchor: "nav.teams",
        title: "Teams",
        body: "Create a team and invite your crew here. Share a session from its dashboard, or a whole car from its device page, and they see only what you share.",
        placement: "right",
        before: revealNavLink("nav.teams"),
        fallback: "center",
      },
    ],
  }),
  announcement("scatter-3d", {
    title: "New: 3D scatter plot",
    body: "Pick a Z Axis channel for a scatter plot gauge and it draws in 3D. Drag to spin it, scroll to zoom, and turn on Show values to read each channel at the cursor.",
    released: "2026-10-29",
    // A dashboard gauge: introduce it on the session dashboard. The
    // gauges are drawn by Flutter, so there is no anchor for a hint.
    route: "/eventdevice/:id",
    video: true,
  }),
  announcement("telemetry-alerts", {
    title: "New: Telemetry alerts",
    body: "Set a rule on any channel and Podium tells you when it crosses the line. Device alerts run all the time, even when nobody is watching: they log every alert and can send a note to Slack or Discord. Dashboard alerts work only while their dashboard is open. Every device gets one device alert free.",
    released: "2026-11-02",
    // Introduced on the home page, where everyone lands: device alerts are
    // set on the device page, dashboard alerts on any dashboard.
    route: "/",
    video: true,
  }),
];
